import {
  CheckboxController,
  Collapse,
  GroupedController,
  NumberController,
  ReactSelectController,
  Text,
} from "@pushpress/shared-components";
import React from "react";
import { type Control, useWatch } from "react-hook-form";
import {
  attendanceLimitOptions,
  classAccessOptions,
  intervalTypeOptions,
  sessionNonRecurringOptions,
  sessionRecurringOptions,
} from "../PlanDrawer.utils";
import { LimitCheckinsType, PlanType } from "../../../../apis/subscriptionApi";
import { PlanForm } from "../PlanDrawer.types";
import ClassTypeSelect from "../../../../components/controllers/ClassTypeSelect";

interface ClassesSectionProps {
  control: Control<PlanForm>;
  planType: PlanType;
}

const ClassesSection = (props: ClassesSectionProps) => {
  const { control } = props;

  const {
    classesVisible,
    attendanceLimitVisible,
    sessionsCountVisible,
    sessionsOptions,
    sessionsVisible,
    sessionsExpireVisible,
    sessionsExpireInVisible,
  } = useClassesSection(props);

  return (
    <div>
      <Text
        component="h2"
        variant="heading-5"
        weight="600"
        color="wolf-800"
        className="mt-4"
      >
        Classes & open gym
      </Text>
      <Text variant="body-md" color="wolf-600" className="mt-1">
        Add class and open gym access
      </Text>
      <ReactSelectController
        control={control}
        name="classAccess"
        label="Class access"
        className="mt-3"
        hideLabel
        options={classAccessOptions}
      />
      <Collapse in={classesVisible}>
        <ClassTypeSelect
          control={control}
          name="classes"
          label="Classes"
          hideLabel
          isMulti
          hideDropdownIndicator
          isClearable
          required
          placeholder="— Select class types —"
        />
      </Collapse>
      <CheckboxController
        control={control}
        name="openGym"
        label="Open gym"
        className="mt-1 mb-4"
      />
      <Collapse in={attendanceLimitVisible}>
        <ReactSelectController
          control={control}
          name="attendanceLimit"
          label="Attendance limit"
          required
          options={attendanceLimitOptions}
        />
      </Collapse>
      <Collapse in={sessionsCountVisible}>
        <NumberController
          control={control}
          name="sessionsCount"
          label="Sessions"
          required
          min={1}
        />
      </Collapse>
      <Collapse in={sessionsExpireVisible}>
        <CheckboxController
          control={control}
          name="sessionsExpire"
          label="Punchcard expires"
          className="mb-4"
        />
      </Collapse>
      <Collapse in={sessionsVisible}>
        <GroupedController
          label="Sessions"
          flexItems={["0 0 120px", "1"]}
          required
        >
          <NumberController
            control={control}
            name="sessionsCount"
            label="Sessions count"
            required
            min={1}
          />
          <ReactSelectController
            control={control}
            name="sessionsPeriod"
            label="Sessions interval"
            required
            hideLabel
            options={sessionsOptions}
          />
        </GroupedController>
      </Collapse>
      <Collapse in={sessionsExpireInVisible}>
        <GroupedController
          label="Punchcard expire in"
          flexItems={["0 0 120px", "1"]}
          required
        >
          <NumberController
            control={control}
            name="sessionsExpireCount"
            label="Sessions expire in"
            required
            min={1}
          />
          <ReactSelectController
            control={control}
            name="sessionsExpireInterval"
            label="Sessions expire in interval"
            required
            hideLabel
            options={intervalTypeOptions}
          />
        </GroupedController>
      </Collapse>
    </div>
  );
};

export function useClassesSection(props: ClassesSectionProps) {
  const { control, planType } = props;

  const [attendanceLimit, classAccess, openGym, sessionsExpire] = useWatch({
    control,
    name: ["attendanceLimit", "classAccess", "openGym", "sessionsExpire"],
  });

  let classesVisible = false;
  let attendanceLimitVisible = false;
  let sessionsCountVisible = false;
  let sessionsVisible = false;
  let sessionsExpireVisible = false;
  let sessionsExpireInVisible = false;
  let sessionsOptions: {
    label: string;
    value: LimitCheckinsType;
  }[] = [];

  if (planType === "recurring") {
    attendanceLimitVisible = true;
    sessionsOptions = sessionRecurringOptions;
  } else if (planType === "non-recurring") {
    attendanceLimitVisible = true;
    sessionsOptions = sessionNonRecurringOptions;
  } else if (planType === "session-pack") {
    sessionsCountVisible = true;
    sessionsExpireVisible = true;
  }
  if (planType === "recurring" && attendanceLimit.value === "limited") {
    sessionsVisible = true;
  }
  if (planType === "non-recurring" && attendanceLimit.value === "limited") {
    sessionsVisible = true;
  }
  if (classAccess.value === "some") {
    classesVisible = true;
  }
  if (planType === "session-pack" && sessionsExpire) {
    sessionsExpireInVisible = true;
  }
  if (classAccess.value === "none" && !openGym) {
    attendanceLimitVisible = false;
    sessionsVisible = false;
    sessionsCountVisible = false;
    sessionsExpireVisible = false;
    sessionsExpireInVisible = false;
  }

  return {
    classesVisible,
    attendanceLimitVisible,
    sessionsCountVisible,
    sessionsOptions,
    sessionsVisible,
    sessionsExpireVisible,
    sessionsExpireInVisible,
  };
}

export default ClassesSection;
